import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [authData, setAuthDataState] = useState(null);
  const [authLoading, setAuthLoading] = useState(true); // Track if we're still checking auth

  // Load auth data from localStorage on mount and check expiration
  useEffect(() => {
    const checkAndLoadAuth = () => {
      const storedAuth = localStorage.getItem('authData');
      if (storedAuth) {
        try {
          const authData = JSON.parse(storedAuth);
          
          // Check if session has expired (24 hours = 86400000 milliseconds)
          const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;
          const loginTime = authData.loginTime || 0;
          const now = Date.now();
          
          if (now - loginTime > TWENTY_FOUR_HOURS) {
            // Session expired, clear auth data
            console.log('Session expired (24 hours), requiring re-login');
            localStorage.removeItem('authData');
            setAuthDataState(null);
            setAuthLoading(false);
            return;
          }
          
          // Session is still valid
          setAuthDataState(authData);
        } catch (err) {
          console.error('Error parsing stored auth data:', err);
          localStorage.removeItem('authData');
          setAuthDataState(null);
        }
      }
      setAuthLoading(false); // Finished checking auth
    };
    
    // Check on mount
    checkAndLoadAuth();
    
    // Check every 5 minutes to catch expiration during active use
    const interval = setInterval(checkAndLoadAuth, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  const setAuthData = (data) => {
    if (data) {
      // Add login timestamp to track session expiration
      const authDataWithTimestamp = {
        ...data,
        loginTime: Date.now()
      };
      setAuthDataState(authDataWithTimestamp);
      localStorage.setItem('authData', JSON.stringify(authDataWithTimestamp));
    } else {
      setAuthDataState(null);
      localStorage.removeItem('authData');
    }
  };

  const logout = () => {
    setAuthData(null);
  };

  const isAuthenticated = () => {
    if (!authData || authData.userId < 1 || authData.userId > 10) {
      return false;
    }
    
    // Check if session has expired (24 hours = 86400000 milliseconds)
    const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;
    const loginTime = authData.loginTime || 0;
    const now = Date.now();
    
    if (now - loginTime > TWENTY_FOUR_HOURS) {
      // Session expired, clear auth data
      console.log('Session expired (24 hours), requiring re-login');
      setAuthData(null);
      return false;
    }
    
    return true;
  };

  return (
    <AuthContext.Provider value={{ authData, setAuthData, logout, isAuthenticated, authLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

